// src/components/Admin/MasterformSubmissions.jsx — submissions of the public /masterform page
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  TablePagination, TextField, MenuItem, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, Chip, CircularProgress, InputAdornment, Snackbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  FileDownload as ExcelIcon,
  Description as DocIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { publicAPI } from '../../services/api';
import { printProcesVerbal } from './masterformPrint';
import { useAdmin } from '../../contexts/AdminContext';
import Loading from '../common/Loading';

const DOCS = [
  { field: 'cin_file',            flag: 'has_cin', label: 'CIN' },
  { field: 'releve_note_s1_file', flag: 'has_s1',  label: 'Relevé S1' },
  { field: 'releve_note_s2_file', flag: 'has_s2',  label: 'Relevé S2' },
  { field: 'releve_note_s3_file', flag: 'has_s3',  label: 'Relevé S3' },
];

const DETAIL_FIELDS = [
  ['full_name', 'Nom et Prénom'], ['master_name', 'Master'], ['apogee_number', 'N° Apogée'],
  ['national_student_id', 'CNE'], ['national_card_id', 'CIN'], ['phone', 'Téléphone'], ['email', 'Email'],
  ['birth_date', 'Date de naissance'], ['birth_place', 'Lieu de naissance'], ['license_obtained', 'Licence obtenue'],
  ['supervisor_name', 'Encadrant'], ['registration_date', "Année d'inscription"], ['discussion_date', 'Date de soutenance'],
  ['research_title', 'Titre de recherche'], ['notes', 'Notes'],
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const MasterformSubmissions = () => {
  const { admin } = useAdmin();
  const isSuperAdmin = admin?.role === 'SUPER_ADMIN';

  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [search, setSearch]       = useState('');
  const [master, setMaster]       = useState('');
  const [page, setPage]           = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [selected, setSelected]   = useState(null);
  const [docLoading, setDocLoading] = useState(null);
  const [toDelete, setToDelete]   = useState(null);
  const [toast, setToast]         = useState(null);

  const fetchRows = async () => {
    try {
      setLoading(true); setError(null);
      const data = await publicAPI.getMasterformSubmissions();
      setRows(data.submissions || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Échec du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, []);

  const masterCounts = useMemo(() => {
    const counts = {};
    rows.forEach(r => { counts[r.master_name] = (counts[r.master_name] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r =>
      (!master || r.master_name === master) &&
      (!q || [r.full_name, r.apogee_number, r.national_card_id, r.national_student_id, r.supervisor_name, r.phone]
        .some(v => (v || '').toLowerCase().includes(q)))
    );
  }, [rows, search, master]);

  const openDocument = async (id, field) => {
    // Open the tab synchronously so popup blockers allow it, then fill it once the blob arrives
    const win = window.open('', '_blank');
    setDocLoading(`${id}-${field}`);
    try {
      const blob = await publicAPI.getMasterformDocument(id, field);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      win?.close();
      setToast({ severity: 'error', message: 'Document introuvable' });
    } finally {
      setDocLoading(null);
    }
  };

  const handlePrint = (r) => {
    if (!printProcesVerbal(r)) {
      setToast({ severity: 'warning', message: 'Autorisez les fenêtres pop-up pour imprimer le PV' });
    }
  };

  const handleDelete = async () => {
    try {
      await publicAPI.deleteMasterformSubmission(toDelete.id);
      setRows(rs => rs.filter(r => r.id !== toDelete.id));
      setToast({ severity: 'success', message: 'Demande supprimée' });
    } catch (e) {
      setToast({ severity: 'error', message: e.response?.data?.error || 'Échec de la suppression' });
    } finally {
      setToDelete(null); setSelected(null);
    }
  };

  const exportExcel = () => {
    const data = filtered.map(r => ({
      'ID': r.id,
      'Date de dépôt': fmtDate(r.created_at),
      ...Object.fromEntries(DETAIL_FIELDS.map(([k, label]) => [
        label, ['birth_date', 'discussion_date'].includes(k) ? fmtDate(r[k]) : (r[k] || ''),
      ])),
      'Documents': DOCS.filter(d => r[d.flag]).map(d => d.label).join(', '),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Demandes Master');
    XLSX.writeFile(wb, `demandes_master_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (loading) return <Loading message="Chargement des demandes master..." />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Demandes de soutenance Master</Typography>
          <Typography variant="body2" color="text.secondary">
            Formulaire public : /masterform · {rows.length} demandes au total
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchRows}>Actualiser</Button>
          <Button variant="contained" startIcon={<ExcelIcon />} onClick={exportExcel} disabled={!filtered.length} sx={{ boxShadow: 'none' }}>
            Excel ({filtered.length})
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Count per master — click to filter */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {masterCounts.map(([name, count]) => (
          <Chip
            key={name} label={`${name} · ${count}`} size="small"
            color={master === name ? 'primary' : 'default'}
            variant={master === name ? 'filled' : 'outlined'}
            onClick={() => { setMaster(master === name ? '' : name); setPage(0); }}
          />
        ))}
      </Box>

      <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Nom, Apogée, CIN, CNE, encadrant, téléphone..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField
            select size="small" label="Master" value={master} sx={{ minWidth: 260 }}
            onChange={(e) => { setMaster(e.target.value); setPage(0); }}
          >
            <MenuItem value="">Tous les masters</MenuItem>
            {masterCounts.map(([name, count]) => <MenuItem key={name} value={name}>{name} ({count})</MenuItem>)}
          </TextField>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['Dépôt', 'Nom et Prénom', 'Apogée', 'CIN', 'Master', 'Soutenance', 'Documents', ''].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: '#475569' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.full_name}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.apogee_number}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{r.national_card_id}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>{r.master_name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.discussion_date)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {DOCS.filter(d => r[d.flag]).map(d => (
                        <Tooltip key={d.field} title={d.label}>
                          <span>
                            <IconButton size="small" onClick={() => openDocument(r.id, d.field)} disabled={docLoading === `${r.id}-${d.field}`}>
                              {docLoading === `${r.id}-${d.field}` ? <CircularProgress size={16} /> : <DocIcon fontSize="small" color="primary" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      ))}
                      {!DOCS.some(d => r[d.flag]) && <Typography variant="caption" color="text.disabled">—</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Imprimer le PV (محضر المناقشة)"><IconButton size="small" color="primary" onClick={() => handlePrint(r)}><PrintIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Détails"><IconButton size="small" onClick={() => setSelected(r)}><ViewIcon fontSize="small" /></IconButton></Tooltip>
                    {isSuperAdmin && (
                      <Tooltip title="Supprimer"><IconButton size="small" color="error" onClick={() => setToDelete(r)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!filtered.length && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucune demande</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={filtered.length} page={page} rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]} labelRowsPerPage="Lignes"
        />
      </Paper>

      {/* Details */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {selected.full_name}
              <Typography variant="body2" color="text.secondary">Demande n° {selected.id} · déposée le {fmtDate(selected.created_at)}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                {DETAIL_FIELDS.map(([k, label]) => (
                  <Grid item xs={12} sm={['research_title', 'notes', 'master_name'].includes(k) ? 12 : 6} key={k}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'pre-wrap' }} dir="auto">
                      {['birth_date', 'discussion_date'].includes(k) ? fmtDate(selected[k]) : (selected[k] || '—')}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
                {DOCS.map(d => (
                  <Button key={d.field} variant="outlined" size="small" startIcon={<DocIcon />}
                    disabled={!selected[d.flag] || docLoading === `${selected.id}-${d.field}`}
                    onClick={() => openDocument(selected.id, d.field)}>
                    {d.label}
                  </Button>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              {isSuperAdmin && <Button color="error" onClick={() => setToDelete(selected)}>Supprimer</Button>}
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setSelected(null)}>Fermer</Button>
              <Button variant="contained" startIcon={<PrintIcon />} onClick={() => handlePrint(selected)} sx={{ boxShadow: 'none' }}>
                Imprimer le PV
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!toDelete} onClose={() => setToDelete(null)}>
        <DialogTitle>Supprimer cette demande ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            La demande de <b>{toDelete?.full_name}</b> (n° {toDelete?.id}) et ses documents seront supprimés définitivement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={handleDelete} sx={{ boxShadow: 'none' }}>Supprimer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
};

export default MasterformSubmissions;
